import {
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
} from '@mui/material';
import { FormikHandlers, FormikHelpers, FormikState } from 'formik';
import { ModifiedFormControlProps } from '../../types/ModifiedFormControlProps';

/**
 * The properties for an enum select field
 */
type FormikEnumSelectProps<
  Enum extends object,
  T extends object,
  K extends keyof T & string,
> = {
  /**
   * The formik object that's connected to the form that the select field will be within
   */
  formik: FormikState<T> & FormikHandlers & FormikHelpers<T>;

  /**
   * The name of the field that's associated with this select field
   */
  name: K;

  /**
   * The enum to populate the select field with.
   */
  enum: Enum;

  /**
   * A label that's used for the select field
   */
  label: string;

  /**
   * (Optional) This prop helps users to fill forms faster, especially on mobile devices.
   * The name can be confusing, as it's more like an autofill.
   * You can learn more about it [following the specification](https://html.spec.whatwg.org/multipage/form-control-infrastructure.html#autofill).
   */
  autoComplete?: string;

  /**
   * (Optional) Whether the form field is disabled. The field will always disable while
   *  the form is being submitted, regardless of this value.
   */
  disabled?: boolean;
} & ModifiedFormControlProps;

/**
 * A formik field to convert an enum into a select. The enum will be formatted to be used as the display value. Spaces will be
 *  added automatically according to pascal casing. Setting the initial value to -1 will cause the select to not be pre-populated
 *  with any selection.
 */
const FormikEnumSelect = <
  Enum extends object,
  T extends object,
  K extends keyof T & string,
>(
  props: FormikEnumSelectProps<Enum, T, K>
) => {
  const strName = String(props.name);
  const labelId = `${strName}-select-label-id`;
  const enumStr: string[] = Object.values(props.enum).filter(
    (eVal) => typeof eVal === 'string'
  );
  const enumNum: number[] = Object.values(props.enum).filter(
    (eVal) => typeof eVal === 'number'
  );
  const enumStrMap = new Map<string, number>([
    ['', -1],
    ...enumStr.map<[string, number]>((e, i) => [e, enumNum[i]]),
  ]);
  const enumNumMap = new Map<number, string>([
    [-1, ''],
    ...enumNum.map<[number, string]>((e, i) => [e, enumStr[i]]),
  ]);

  return (
    <FormControl
      id={strName}
      disabled={props.formik.isSubmitting || props.disabled}
      error={
        props.formik.touched[props.name] &&
        Boolean(props.formik.errors[props.name] as string)
      }
      {...props}
    >
      <InputLabel id={labelId}>{props.label}</InputLabel>
      <Select
        id={`${strName}-select`}
        label={props.label}
        labelId={labelId}
        name={props.name}
        autoComplete={props.autoComplete}
        value={enumNumMap.get(props.formik.values[props.name] as number)}
        onChange={(v) => {
          props.formik.setFieldValue(
            props.name,
            enumStrMap.get(v.target.value as string)
          );
        }}
      >
        {enumStr.map((eVal) => {
          const formatted = (eVal as string)
            .replace(/([a-z])([A-Z])/g, '$1 $2')
            // Force the word "of" (in any case) to be lowercase
            .replace(/\b[oO][fF]\b/g, 'of');

          return (
            <MenuItem key={eVal} value={eVal}>
              {formatted}
            </MenuItem>
          );
        })}
      </Select>
      <FormHelperText
        sx={{
          marginBottom: '1.5em',
        }}
      >
        {
          (props.formik.touched[props.name] &&
            props.formik.errors[props.name]) as string
        }
      </FormHelperText>
    </FormControl>
  );
};

export default FormikEnumSelect;
